import { Injectable } from '@angular/core'
import { getState } from '@app/srv/store.service'
import {
  LocalDate,
  LocalTime,
  localTime,
  LocalTimeFormatter,
  StringMap,
  UnixTimestamp,
  UnixTimestampMillis,
} from '@naturalcycles/js-lib'
import { LANG } from '@naturalcycles/shared'
import { AndroidDatePicker } from '@src/typings/capacitor.js'

export interface Weekday {
  fullname: string
  shortname: string
  isoNumber: number
}

export enum DateFormat {
  SHORT = 'SHORT',
  MEDIUM = 'MEDIUM',
  MEDIUM_WITH_TIME = 'MEDIUM_WITH_TIME',
  LONG = 'LONG',
  FULL = 'FULL',
  MONTH_YEAR = 'MONTH_YEAR',
  WEEKDAY = 'WEEKDAY',
  WEEKDAY_SHORT = 'WEEKDAY_SHORT',
  WEEKDAY_DATE = 'WEEKDAY_DATE',
  WEEKDAY_DATE_LONG = 'WEEKDAY_DATE_LONG',
  DAY_MONTH = 'DAY_MONTH',
  DAY_MONTH_SHORT = 'DAY_MONTH_SHORT',
  DAY_MONTH_TIME = 'DAY_MONTH_TIME',
  TIME = 'TIME',
  DAY = 'DAY',
  MONTH = 'MONTH',
}

const dateFormatMap: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  [DateFormat.SHORT]: {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
  },
  [DateFormat.MEDIUM]: {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  },
  [DateFormat.MEDIUM_WITH_TIME]: {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false, // To omit the AM/PM space in the multiple temps modal
  },
  [DateFormat.LONG]: {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  },
  [DateFormat.FULL]: {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  [DateFormat.MONTH_YEAR]: {
    year: 'numeric',
    month: 'long',
  },
  [DateFormat.WEEKDAY]: {
    weekday: 'long',
  },
  [DateFormat.WEEKDAY_SHORT]: {
    weekday: 'short',
  },
  [DateFormat.WEEKDAY_DATE]: {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  },
  [DateFormat.WEEKDAY_DATE_LONG]: {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  },
  [DateFormat.DAY_MONTH]: {
    day: 'numeric',
    month: 'long',
  },
  [DateFormat.DAY_MONTH_SHORT]: {
    day: 'numeric',
    month: 'short',
  },
  [DateFormat.DAY_MONTH_TIME]: {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: 'numeric',
  },
  [DateFormat.TIME]: {
    hour: 'numeric',
    minute: 'numeric',
  },
  [DateFormat.DAY]: {
    day: 'numeric',
  },
  [DateFormat.MONTH]: {
    month: 'long',
  },
}

/**
 * Returns HH:mm
 */
export const hhmmFormat: LocalTimeFormatter = lt => {
  const hour = String(lt.hour).padStart(2, '0')
  const min = String(lt.minute).padStart(2, '0')
  return `${hour}:${min}`
}

let dateService: DateService

// shortcut
export function localizeDate(date: LocalDate, format = DateFormat.MEDIUM, locale?: string): string {
  return dateService.localizeDate(date, format, locale)
}

// [localeName + DateFormat_ID] => DateTimeFormat
const dateTimeFormatCache: StringMap<Intl.DateTimeFormat> = {}

@Injectable({ providedIn: 'root' })
export class DateService {
  constructor() {
    dateService = this
  }

  public getWeekdays(locale: string, firstDayOfWeek?: number): Weekday[] {
    const longFormatter = new Intl.DateTimeFormat(locale, { weekday: 'long' })
    const shortFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short' })

    const weekdays: Weekday[] = Array.from({ length: 7 }, (_, i) => {
      // Create a date and set it to Sunday (day 0)
      const date = new Date()
      date.setDate(date.getDate() - date.getDay() + i)

      return {
        fullname: longFormatter.format(date),
        shortname: shortFormatter.format(date),
        isoNumber: date.getDay() === 0 ? 7 : date.getDay(),
      }
    })

    if (firstDayOfWeek === 7) {
      // Sunday, do nothing
    } else if (firstDayOfWeek === 6) {
      // Saturday, move saturday to beginning
      weekdays.unshift(weekdays.pop()!)
    } else {
      // Monday, move sunday to end
      weekdays.push(weekdays.shift()!)
    }

    return weekdays
  }

  /**
   * When you want to localize a date that doesn't depend on timezone (calendar dates)
   */
  public localizeDate(date: LocalDate, format = DateFormat.MEDIUM, locale?: string): string {
    const unixTimestamp = Date.UTC(date.year, date.month - 1, date.day) as UnixTimestampMillis
    const localizedDate = this._localizeDate(unixTimestamp, format, true, locale)

    return format === DateFormat.WEEKDAY_SHORT ? localizedDate.slice(0, 3) : localizedDate
  }

  /**
   * When you want to localize a time dependant date
   */
  public localizeDateTime(date: LocalTime, format = DateFormat.MEDIUM, locale?: string): string {
    return this._localizeDate(date.unixMillis, format, false, locale)
  }

  private _localizeDate(
    date: UnixTimestampMillis,
    format = DateFormat.MEDIUM,
    timezoneless = true,
    locale?: string,
  ): string {
    locale ||= getState().ui.locale

    // es-US date locales don't support short month formats for some reason
    // example:
    // new Date().toLocaleDateString('es-US', { day: 'numeric', month: 'short', year: 'numeric' })
    // outputs: 13 de diciembre de 2019
    // new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
    // outputs: 13 dic. 2019
    if (locale === LANG.es_US) locale = 'es-ES'

    const output = (dateTimeFormatCache[locale + format] ||= new Intl.DateTimeFormat(locale, {
      ...dateFormatMap[format],
      timeZone: timezoneless ? 'UTC' : undefined,
      calendar: 'gregory',
    })).format(date)

    return output
  }

  /**
   * Returns 'HH:mm'
   */
  public async getTimeOnAndroid(time: string): Promise<string> {
    return await new Promise(resolve => {
      const lt = this.getLocalTimeFromHHMM(time)
      void AndroidDatePicker.showTimePicker({
        hour: lt.hour,
        minute: lt.minute,
      }).then(date => {
        if (!date) return resolve(time)

        resolve(localTime.now().setHour(date.hour).setMinute(date.minute).format(hhmmFormat))
      })
    })
  }

  private getLocalTimeFromHHMM(time: string): LocalTime {
    const [hour, minute] = time.split(':') as [string, string]
    return localTime.now().setHour(parseInt(hour)).setMinute(parseInt(minute))
  }

  public appTimestampToT3Timestamp(): UnixTimestamp {
    const utcOffset = localTime.now().getUTCOffsetMinutes()
    return localTime.now().plusMinutes(utcOffset).unix
  }

  public t3TimestampToAppTimestamp(timestamp: UnixTimestamp): UnixTimestamp {
    const utcOffset = localTime.now().getUTCOffsetMinutes()
    return localTime(timestamp).minusMinutes(utcOffset).unix
  }
}
